import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, MessageCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getWhatsAppLink } from "@/lib/contact";
import { useLanguage } from "@/lib/i18n";

const serviceMenuItems = [
  { name: "Búsqueda de productos", nameEn: "Product sourcing", path: "/services/busqueda-productos" },
  { name: "Inspección de origen", nameEn: "Origin inspection", path: "/services/inspeccion-origen" },
  { name: "Inspecciones en China", nameEn: "Inspections in China", path: "/services/inspecciones-china", featured: true },
  { name: "Renta de bodegas", nameEn: "Warehouse rental", path: "/services/renta-bodegas", featured: true },
  { name: "Verificación de empresas", nameEn: "Company verification", path: "/services/verificacion-empresas" },
  { name: "Cierre de negociaciones", nameEn: "Negotiation closing", path: "/services/cierre-negociaciones", featured: true },
  { name: "Despacho aduanero", nameEn: "Customs clearance", path: "/services/despacho-aduanero", featured: true },
  { name: "Transporte internacional", nameEn: "International transportation", path: "/services/transporte-internacional" },
  { name: "Importación y exportación", nameEn: "Import and export", path: "/services/importacion-exportacion" },
  { name: "Logística integral", nameEn: "End-to-end logistics", path: "/services/logistica-integral" },
];

const CLIENT_PORTAL_URL = "https://nnexio.com";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const isServicesActive = location.pathname.startsWith("/services");
  const { isEnglish, setLanguage, t } = useLanguage();

  const navLinks = [
    { name: t("nav.home", "Inicio"), path: "/" },
    { name: t("nav.about", "Nosotros"), path: "/about" },
    { name: t("nav.contact", "Contacto"), path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(location.pathname.startsWith("/services"));
  }, [location]);

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-6xl z-50 transition-all duration-300 ${
          scrolled
            ? "bg-zinc-950/95 backdrop-blur-xl border-zinc-800/80"
            : "bg-zinc-950/75 backdrop-blur-md border-zinc-800/50"
        } border rounded-full px-4 md:px-6 py-3`}
      >
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link" aria-label="Navi Cargo, inicio">
            <img
              src="/navicargo-logo-white-transparent.png"
              alt="Navi Cargo"
              className="block w-[112px] sm:w-[124px] h-auto shrink-0 object-contain"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.slice(0, 2).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`nav-link px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                  location.pathname === link.path
                    ? "text-[#22C55E]"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <div className="flex items-center">
                <Link
                  to="/services"
                  data-testid="nav-link-servicios"
                    className={`nav-link px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                    isServicesActive ? "text-[#22C55E]" : "text-zinc-400 hover:text-white"
                  }`}
                >
                   {t("nav.services", "Servicios")}
                </Link>
                <button
                  type="button"
                   aria-label={t("nav.openServices", "Abrir submenú de servicios")}
                  aria-expanded={servicesOpen}
                  onClick={() => setServicesOpen((open) => !open)}
                  className={`p-1 transition-colors ${isServicesActive ? "text-[#22C55E]" : "text-zinc-500 hover:text-white"}`}
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                </button>
              </div>

              {servicesOpen && (
                <div
                  data-testid="services-submenu"
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-72"
                >
                  <div className="rounded-2xl border border-zinc-800 bg-zinc-950/95 p-2 shadow-2xl shadow-black/40 backdrop-blur-xl">
                    <div className="px-3 py-2">
                       <p className="text-[10px] uppercase tracking-[0.2em] text-[#22C55E]">{t("nav.solutions", "Soluciones Navicargo")}</p>
                       <p className="text-xs text-zinc-500 mt-1">{t("nav.chooseService", "Elige el servicio para tu operación")}</p>
                    </div>
                    {serviceMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors ${
                          location.pathname === item.path
                            ? "bg-[#22C55E]/10 text-[#22C55E]"
                            : "text-zinc-300 hover:bg-zinc-800/70 hover:text-white"
                        }`}
                      >
                         <span>{isEnglish ? item.nameEn : item.name}</span>
                        {item.featured && (
                          <span className="rounded-full bg-[#22C55E]/10 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#22C55E]">
                             {t("nav.new", "Nuevo")}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              data-testid="nav-link-contacto"
              className={`nav-link px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                location.pathname === "/contact" ? "text-[#22C55E]" : "text-zinc-400 hover:text-white"
              }`}
            >
               {t("nav.contact", "Contacto")}
            </Link>
            <a
              href={CLIENT_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-link-clientes"
               aria-label={t("nav.clientAccess", "Acceso para clientes en nnexio.com")}
               title={`${t("nav.clients", "Clientes")} · nnexio.com`}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-400 hover:text-[#22C55E] transition-colors"
            >
               {t("nav.clients", "Clientes")}
            </a>
          </div>

           <div className="hidden md:flex items-center gap-3 shrink-0">
            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="get-quote-btn"
                className="bg-[#22C55E] text-black hover:bg-[#22C55E]/90 btn-glow rounded-full px-5 font-semibold"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t("nav.quote", "Cotizar por WhatsApp")}
              </Button>
            </a>
             <button
               type="button"
               onClick={() => setLanguage(isEnglish ? "es" : "en")}
               aria-label={isEnglish ? "Cambiar a español" : "Switch to English"}
               title={isEnglish ? "Cambiar a español" : "Switch to English"}
               className="inline-flex h-9 items-center gap-0.5 rounded-full border border-zinc-700/80 bg-zinc-900/80 p-1 text-[9px] font-bold uppercase tracking-[0.12em] text-zinc-400 shadow-inner shadow-black/30 transition-colors hover:border-[#22C55E]/50"
             >
               <span className={`inline-flex h-7 min-w-[27px] items-center justify-center rounded-full px-1.5 transition-colors ${!isEnglish ? "bg-[#22C55E]/15 text-[#22C55E]" : "text-zinc-500 hover:text-zinc-300"}`}>
                 ES
               </span>
               <span className="px-0.5 text-zinc-700">/</span>
               <span className={`inline-flex h-7 min-w-[27px] items-center justify-center rounded-full px-1.5 transition-colors ${isEnglish ? "bg-[#22C55E]/15 text-[#22C55E]" : "text-zinc-500 hover:text-zinc-300"}`}>
                 EN
               </span>
             </button>
          </div>

          <button
            className="lg:hidden p-2 text-zinc-400 hover:text-white transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label={isOpen ? t("nav.closeMenu", "Cerrar menú") : t("nav.openMenu", "Abrir menú")}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-menu">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-20 left-4 right-4 max-h-[calc(100vh-6rem)] overflow-y-auto bg-zinc-900/95 backdrop-blur-xl border border-zinc-800 rounded-2xl p-6 mobile-menu">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] rounded-lg transition-colors ${location.pathname === "/" ? "text-[#22C55E] bg-zinc-800/50" : "text-zinc-300 hover:text-white hover:bg-zinc-800/30"}`}
              >
                 {t("nav.home", "Inicio")}
              </Link>
              <Link
                to="/about"
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] rounded-lg transition-colors ${location.pathname === "/about" ? "text-[#22C55E] bg-zinc-800/50" : "text-zinc-300 hover:text-white hover:bg-zinc-800/30"}`}
              >
                 {t("nav.about", "Nosotros")}
              </Link>

              <div className="rounded-xl bg-zinc-950/40">
                <div className="flex items-center justify-between">
                  <Link
                    to="/services"
                    data-testid="mobile-nav-link-servicios"
                    className={`flex-1 px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] rounded-lg transition-colors ${isServicesActive ? "text-[#22C55E]" : "text-zinc-300 hover:text-white hover:bg-zinc-800/30"}`}
                  >
                     {t("nav.services", "Servicios")}
                  </Link>
                  <button
                    type="button"
                     aria-label={t("nav.openServices", "Abrir submenú móvil de servicios")}
                    aria-expanded={mobileServicesOpen}
                    onClick={() => setMobileServicesOpen((open) => !open)}
                    className="p-3 text-zinc-400 hover:text-white"
                  >
                    <ChevronDown className={`w-5 h-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                </div>
                {mobileServicesOpen && (
                  <div className="border-t border-zinc-800/70 px-2 pb-2 pt-1">
                    {serviceMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                          location.pathname === item.path ? "text-[#22C55E] bg-[#22C55E]/10" : "text-zinc-400 hover:bg-zinc-800/60 hover:text-white"
                        }`}
                      >
                         <span>{isEnglish ? item.nameEn : item.name}</span>
                         {item.featured && <span className="text-[9px] uppercase tracking-wider text-[#22C55E]">{t("nav.new", "Nuevo")}</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/contact"
                className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] rounded-lg transition-colors ${location.pathname === "/contact" ? "text-[#22C55E] bg-zinc-800/50" : "text-zinc-300 hover:text-white hover:bg-zinc-800/30"}`}
              >
                 {t("nav.contact", "Contacto")}
              </Link>
              <a
                href={CLIENT_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-link-clientes"
                 aria-label={t("nav.clientAccess", "Acceso para clientes en nnexio.com")}
                className="flex items-center justify-between px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800/30 transition-colors"
              >
                 <span>{t("nav.clients", "Clientes")}</span>
              </a>
              <div className="border-t border-zinc-800 my-4" />
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="mt-2">
                <Button
                  data-testid="mobile-get-quote-btn"
                  className="w-full bg-[#22C55E] text-black hover:bg-[#22C55E]/90 btn-glow rounded-full py-6 font-semibold text-base"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                   {t("nav.quote", "Cotizar por WhatsApp")}
                </Button>
              </a>
              <button
                type="button"
                onClick={() => setLanguage(isEnglish ? "es" : "en")}
                aria-label={isEnglish ? "Cambiar a español" : "Switch to English"}
                className="mt-3 inline-flex self-start items-center gap-2 rounded-full border border-zinc-700/80 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-300"
              >
                <span className={!isEnglish ? "text-[#22C55E]" : "text-zinc-500"}>ES</span>
                <span className="text-zinc-700">/</span>
                <span className={isEnglish ? "text-[#22C55E]" : "text-zinc-500"}>EN</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;